import { Transition } from '@headlessui/react'
import { useEffect, useState } from 'react'
import { Icon } from '../Icon'
import { tm } from '../tm'
import type { SizeKeys } from '../Icon'
import type {
  CSSProperties,
  ChangeEventHandler,
  FocusEventHandler,
  ReactNode,
} from 'react'
import type { FieldMetaState } from 'react-final-form'

const SIZES: Record<
  'small' | 'base' | 'mini',
  Record<'label' | 'input' | 'icon', string> & Record<'iconSize', SizeKeys>
> = {
  mini: {
    label: 'text-xs',
    input: 'h-4 w-4 rounded-md',
    icon: 'left-0',
    iconSize: 'small',
  },
  small: {
    label: 'text-sm',
    input: 'h-4 w-4 rounded-md',
    icon: 'left-0',
    iconSize: 'small',
  },
  base: {
    label: 'text-base',
    input: 'h-6 w-6 rounded-lg',
    icon: 'left-0',
    iconSize: 'normal',
  },
}

export interface CheckboxProps {
  blur?: FocusEventHandler<HTMLInputElement>
  change?: ChangeEventHandler<HTMLInputElement>
  className?: string
  labelClassName?: string
  disabled?: boolean
  focus?: FocusEventHandler<HTMLInputElement>
  formMeta?: FieldMetaState<string>
  id?: string
  label: ReactNode
  name?: string
  required?: boolean
  size?: keyof typeof SIZES
  style?: CSSProperties
  checked: boolean
}

const emptyObject = {}
const noop = () => {}
export function Checkbox({
  blur = noop,
  change = noop,
  className = '',
  labelClassName = '',
  disabled = false,
  focus = noop,
  formMeta = emptyObject,
  id = undefined,
  name = undefined,
  label,
  required = false,
  size = 'base',
  style = emptyObject,
  checked,
}: CheckboxProps) {
  // TODO: create a useError that will handle the error label by itself
  const [error, setError] = useState<string | undefined>(undefined)
  const [isErrorLabelVisible, showErrorLabel] = useState<boolean>(false)

  const sizeStyles = SIZES[size] ?? SIZES.base

  const hasError =
    !!formMeta.error && (!!formMeta.modified || !!formMeta.touched)

  useEffect(() => {
    if (hasError) {
      setError(formMeta.error)
      showErrorLabel(true)
    }
    if (!hasError && isErrorLabelVisible) {
      showErrorLabel(false)
      setTimeout(() => setError(undefined), 350)
    }
    if (hasError && isErrorLabelVisible && !error) {
      setTimeout(() => setError(formMeta.error), 0)
    }
  }, [hasError, error, formMeta.error, isErrorLabelVisible])

  return (
    <label
      htmlFor={id}
      className={tm(
        'relative flex w-full flex-row-reverse items-center justify-end text-base transition-spacing delay-100 duration-300 ease-out hover:cursor-pointer',
        sizeStyles.label,
        hasError && '!mb-10 delay-0 md:!mb-5',
        labelClassName,
      )}
    >
      <span
        className={tm(
          'ml-2',
          hasError &&
            'text-dark-orange [&_*]:text-dark-orange hover:[&_*]:text-dark-orange focus-visible:[&_*]:text-dark-orange',
        )}
      >
        {label}
        {required ? (
          <Icon icon="asterisk" inline size="tiny" className="align-text-top" />
        ) : null}
      </span>
      <span className="relative inline-flex">
        <input
          id={id}
          type="checkbox"
          name={name}
          className={tm(
            'cursor-pointer rounded-lg border border-gray bg-white transition-input duration-300 ease-out checked:border-black checked:bg-black hover:border-dark-gray hover:shadow-input hover:checked:border-black hover:checked:bg-black focus:border-dark-gray focus:shadow-input focus:checked:border-black focus:checked:bg-black [&:checked+svg]:opacity-100',
            sizeStyles.input,
            hasError &&
              'border-dark-orange bg-light-orange text-dark-orange checked:border-dark-orange checked:bg-dark-orange hover:border-dark-orange hover:!shadow-none hover:checked:border-dark-orange hover:checked:bg-dark-orange focus:border-dark-orange focus:!shadow-none focus:checked:border-dark-orange focus:checked:bg-dark-orange',
            disabled &&
              'cursor-not-allowed border-gray bg-light-gray text-dark-gray hover:border-gray hover:!shadow-none focus:border-gray focus:!shadow-none',
            className,
          )}
          style={style}
          disabled={disabled}
          required={required}
          onBlur={blur}
          onChange={change}
          onFocus={focus}
          checked={checked}
        />
        <Icon
          icon="check"
          size={sizeStyles.iconSize}
          className={tm(
            'pointer-events-none absolute text-white opacity-0 transition-opacity duration-300 ease-out',
            sizeStyles.icon,
          )}
        />
      </span>

      <Transition
        show={isErrorLabelVisible}
        as="span"
        className="pointer-events-none absolute -bottom-5 left-0 z-[1] flex h-auto w-full transform-gpu items-center text-xs text-dark-orange transition-transform duration-200 ease-out data-[closed]:-translate-y-1 data-[closed]:opacity-0"
      >
        <Icon icon="warning-round" size="small" className="mr-1" /> {error}
      </Transition>
    </label>
  )
}
